// Shared Accounts period contract — literal business dates (Asia/Kolkata), never via UTC.
export const BUSINESS_TZ = 'Asia/Kolkata';

export const PERIOD_MODES = { RANGE: 'RANGE', RANGE_ALL_TIME: 'RANGE_ALL_TIME', MONTH_SELECTION: 'MONTH_SELECTION' };

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const pad = (n) => String(n).padStart(2, '0');
const parts = (iso) => iso.split('-').map(Number);
const fromUTC = (dt) => `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;

export const isValidDate = (iso) => {
  if (!DATE_RE.test(iso || '')) return false;
  const [y, m, d] = parts(iso);
  return fromUTC(new Date(Date.UTC(y, m - 1, d))) === iso;
};
export const isValidMonth = (m) => MONTH_RE.test(m || '');

export const businessToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

export const addDays = (iso, n) => {
  const [y, m, d] = parts(iso);
  return fromUTC(new Date(Date.UTC(y, m - 1, d + n)));
};
export const monthStart = (iso) => `${iso.slice(0, 7)}-01`;
export const monthEnd = (ym) => {
  const [y, m] = parts(ym);
  return fromUTC(new Date(Date.UTC(y, m, 0)));
};

// Date object (calendar widget, local) <-> literal ISO date
export const dateToIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const isoToDate = (iso) => {
  const [y, m, d] = parts(iso);
  return new Date(y, m - 1, d);
};

export const QUICK_PICKS = [
  { value: 'today', label: 'Today', range: (t) => [t, t] },
  { value: 'yesterday', label: 'Yesterday', range: (t) => [addDays(t, -1), addDays(t, -1)] },
  { value: 'last_7_days', label: 'Last 7 days', range: (t) => [addDays(t, -6), t] },
  { value: 'this_month_to_date', label: 'This month to date', range: (t) => [monthStart(t), t] },
  {
    value: 'last_month', label: 'Last month',
    range: (t) => { const prev = addDays(monthStart(t), -1); return [monthStart(prev), prev]; },
  },
  {
    value: 'this_quarter_to_date', label: 'This quarter to date',
    range: (t) => { const [y, m] = parts(t); return [`${y}-${pad(Math.floor((m - 1) / 3) * 3 + 1)}-01`, t]; },
  },
  {
    value: 'this_financial_year_to_date', label: 'This financial year to date',
    range: (t) => { const [y, m] = parts(t); return [`${m >= 4 ? y : y - 1}-04-01`, t]; },
  },
];

export const presetRange = (preset, today = businessToday()) => {
  const qp = QUICK_PICKS.find((p) => p.value === preset);
  return qp ? qp.range(today) : null;
};

export const DEFAULT_PRESET = 'this_month_to_date';

export const defaultPeriod = () => {
  const [from, to] = presetRange(DEFAULT_PRESET);
  return { from, to, preset: DEFAULT_PRESET, allTime: false };
};

// A preset label is only kept while it still produces exactly these dates.
export const normalizePeriod = ({ from, to, preset, allTime }) => {
  if (allTime) return { from: null, to: null, preset: null, allTime: true };
  if (!isValidDate(from) || !isValidDate(to) || from > to) return null;
  const r = preset && presetRange(preset);
  return { from, to, preset: r && r[0] === from && r[1] === to ? preset : null, allTime: false };
};

export const toApiParams = (p) => {
  if (p.allTime) return { all_time: true };
  const params = { date_from: p.from, date_to: p.to };
  if (p.preset) params.period_preset = p.preset;
  return params;
};

export const formatIso = (iso) => {
  const [y, m, d] = parts(iso);
  return `${pad(d)} ${MONTH_NAMES[m - 1]} ${y}`;
};
export const formatMonth = (ym) => {
  const [y, m] = parts(ym);
  return `${MONTH_LONG[m - 1]} ${y}`;
};
export const formatRange = (from, to) => (from === to ? formatIso(from) : `${formatIso(from)} – ${formatIso(to)}`);

const SOURCE_LABELS = {
  CUSTOM_RANGE: 'Custom range', QUICK_PICK: 'Quick pick', DEFAULT_MTD: 'This month to date',
  MONTH: 'Month', ALL_TIME: 'All time',
};

// Label for what the SERVER says it used.
export const describeResolved = (meta) => {
  if (!meta?.period_source) return null;
  if (meta.period_source === 'ALL_TIME') return 'All time';
  if (meta.period_source === 'MONTH') return `Month: ${formatMonth(meta.period_month || meta.period_start.slice(0, 7))}`;
  if (meta.period_source === 'MONTH_SELECTION' && meta.months?.length) {
    return `${meta.months.length === 1 ? 'Month' : `${meta.months.length} months`}: ${formatMonthList(meta.months)}`;
  }
  const qp = QUICK_PICKS.find((p) => p.value === meta.period_preset);
  const head = meta.period_source === 'QUICK_PICK' && qp ? qp.label : SOURCE_LABELS[meta.period_source];
  return `${head}: ${formatRange(meta.period_start, meta.period_end)}`;
};

export const recentMonths = (count = 24) => {
  const out = [];
  let cursor = monthStart(businessToday());
  for (let i = 0; i < count; i += 1) {
    const ym = cursor.slice(0, 7);
    out.push({ value: ym, label: formatMonth(ym) });
    cursor = monthStart(addDays(cursor, -1));
  }
  return out;
};

// ── Tax month selection (one or more whole filing months) ──
const monthIndex = (ym) => { const [y, m] = parts(ym); return y * 12 + (m - 1); };
const monthFromIndex = (i) => `${Math.floor(i / 12)}-${pad((i % 12) + 1)}`;

export const sortMonths = (list) => [...new Set(list.filter(isValidMonth))].sort();

export const parseMonths = (value) => {
  if (!value) return null;
  const list = String(value).split(',').map((m) => m.trim());
  if (!list.length || list.some((m) => !isValidMonth(m))) return null;
  return sortMonths(list);
};

export const monthsBetween = (fromYm, toYm) => {
  const out = [];
  for (let i = monthIndex(fromYm); i <= monthIndex(toYm); i += 1) out.push(monthFromIndex(i));
  return out;
};

export const isContiguous = (months) =>
  months.every((m, i) => i === 0 || monthIndex(m) === monthIndex(months[i - 1]) + 1);

// Financial year (Apr–Mar) months up to the current month; offset -1 = last FY.
export const financialYearMonths = (offset = 0) => {
  const t = businessToday().slice(0, 7);
  const [y, m] = parts(t);
  const startY = (m >= 4 ? y : y - 1) + offset;
  const all = monthsBetween(`${startY}-04`, `${startY + 1}-03`);
  return all.filter((ym) => ym <= t);
};

const shortMonth = (ym) => { const [y, m] = parts(ym); return `${MONTH_NAMES[m - 1]} ${y}`; };

export const formatMonthList = (months) => {
  if (!months?.length) return '';
  if (months.length === 1) return formatMonth(months[0]);
  if (isContiguous(months)) return `${shortMonth(months[0])} – ${shortMonth(months[months.length - 1])}`;
  return months.map(shortMonth).join(', ');
};
